import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAppContext } from '../context/AppContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { db } from '../firebase';
import { collection, query, where, getDocs, Timestamp, updateDoc, doc, onSnapshot } from 'firebase/firestore';
import { Transaction, PaymentRequest } from '../types';
import { useTranslation } from 'react-i18next';
import {
  EyeIcon,
  EyeSlashIcon,
  QrCodeIcon,
  CreditCardIcon,
  ClockIcon,
  UserIcon,
  ChartBarIcon,
  GiftIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  CalendarIcon,
  IdentificationIcon,
  ShoppingBagIcon,
  PaintBrushIcon,
  DocumentTextIcon,
  PhoneIcon,
  CurrencyRupeeIcon,
  SwatchIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { advertisements, loading, incrementAdView } = useAppContext();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Track screen width for responsive adjustments
  useEffect(() => {
    const updateScreenWidth = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateScreenWidth);
    return () => window.removeEventListener('resize', updateScreenWidth);
  }, []);

  const [showBalance, setShowBalance] = useState(false);
  const [activityData, setActivityData] = useState<Array<{
    month: string;
    earnings: number;
    withdrawals: number;
  }>>([]);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [activityPeriod, setActivityPeriod] = useState<'3months' | '6months' | 'year'>('6months');
  const [activeTooltipIndex, setActiveTooltipIndex] = useState<number | null>(null);
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequest[]>([]);

  // Chat popup state
  const [showChatPopup, setShowChatPopup] = useState(false);
  const [chatMessage, setChatMessage] = useState('');

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Filter advertisements with images
  const slidesWithImages = useMemo(() =>
    advertisements.filter(ad => ad.imageUrl),
    [advertisements]
  );

  // Auto-slide effect
  useEffect(() => {
    if (!isAutoPlaying || slidesWithImages.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) =>
        prev === slidesWithImages.length - 1 ? 0 : prev + 1
      );
    }, 3000); // Change slide every 3 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, slidesWithImages.length]);

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // Minimum swipe distance

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swiped left - next slide
        nextSlide();
      } else {
        // Swiped right - previous slide
        prevSlide();
      }
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  const nextSlide = () => {
    setCurrentSlide((prev) =>
      prev === slidesWithImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prev) =>
      prev === 0 ? slidesWithImages.length - 1 : prev - 1
    );
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Fetch activity data (earnings and withdrawals)
  useEffect(() => {
    const fetchActivityData = async () => {
      if (!user?.id) return;

      try {
        setLoadingActivity(true);

        // Determine number of months based on selected period
        const monthsCount = activityPeriod === '3months' ? 3 : activityPeriod === '6months' ? 6 : 12;

        // Get months based on selected period
        const months = [];
        const now = new Date();
        for (let i = monthsCount - 1; i >= 0; i--) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          months.push({
            month: date.toLocaleString('en-US', { month: 'short' }),
            year: date.getFullYear(),
            startDate: new Date(date.getFullYear(), date.getMonth(), 1),
            endDate: new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59),
          });
        }

        // Fetch all user's transactions (coupons scanned)
        const transactionsRef = collection(db, 'transactions');
        const transactionsQuery = query(
          transactionsRef,
          where('userId', '==', user.id)
        );
        const transactionsSnapshot = await getDocs(transactionsQuery);
        const transactions = transactionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
        })) as Transaction[];

        // Fetch all user's payment requests (withdrawals)
        const paymentsRef = collection(db, 'paymentRequests');
        const paymentsQuery = query(
          paymentsRef,
          where('userId', '==', user.id),
          where('status', 'in', ['approved', 'paid']) // Only count approved/paid withdrawals
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const payments = paymentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
        })) as PaymentRequest[];

        // Calculate monthly data
        const monthlyData = months.map(({ month, startDate, endDate }) => {
          // Calculate earnings (cashback from scanned coupons) - only type 'cashback'
          const monthlyEarnings = transactions
            .filter(t => {
              const txDate = t.createdAt;
              return (
                txDate &&
                txDate >= startDate &&
                txDate <= endDate &&
                t.type === 'cashback' // Only count cashback transactions
              );
            })
            .reduce((sum, t) => sum + (t.amount || 0), 0);

          // Calculate withdrawals (approved/paid payment requests)
          const monthlyWithdrawals = payments
            .filter(p => {
              const payDate = p.createdAt;
              return payDate && payDate >= startDate && payDate <= endDate;
            })
            .reduce((sum, p) => sum + (p.amount || 0), 0);

          return {
            month,
            earnings: monthlyEarnings,
            withdrawals: monthlyWithdrawals,
          };
        });

        setActivityData(monthlyData);

        // Calculate total earnings and withdrawals (all time)
        const allEarnings = transactions
          .filter(t => t.type === 'cashback')
          .reduce((sum, t) => sum + (t.amount || 0), 0);

        const allWithdrawals = payments
          .reduce((sum, p) => sum + (p.amount || 0), 0);

        setTotalEarnings(allEarnings);
        setTotalWithdrawals(allWithdrawals);
      } catch (error) {
        console.error('Error fetching activity data:', error);
        // Set empty data on error
        setActivityData([]);
        setTotalEarnings(0);
        setTotalWithdrawals(0);
      } finally {
        setLoadingActivity(false);
      }
    };

    fetchActivityData();
  }, [user?.id, activityPeriod]);

  // Fetch user's payment requests in real-time for available balance calculation
  useEffect(() => {
    if (!user?.id) return;

    const q = query(
      collection(db, 'paymentRequests'),
      where('userId', '==', user.id),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as any),
      })) as PaymentRequest[];

      setPaymentRequests(requests);
    });

    return () => unsubscribe();
  }, [user?.id]);

  // Calculate unique Bondhu ID
  const bondhuId = useMemo(() => {
    if (!user?.name || !user?.phone || !user?.createdAt) return 'LOADING...';

    try {
      // Split name into first name and last name
      const nameParts = user.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts[nameParts.length - 1] || '';

      // 1. First 2 letters of first name (pad with 0 if less than 2)
      let firstNamePart = firstName.substring(0, 2).toUpperCase();
      while (firstNamePart.length < 2) {
        firstNamePart += '0';
      }

      // 2. Last 4 digits of mobile number
      const phoneDigits = user.phone.replace(/\D/g, ''); // Remove non-digits
      const last4Phone = phoneDigits.slice(-4);

      // 3. Joining date (DDMMYY - only last 2 digits of year)
      const joinDate = user.createdAt;
      const day = String(joinDate.getDate()).padStart(2, '0');
      const month = String(joinDate.getMonth() + 1).padStart(2, '0');
      const year = String(joinDate.getFullYear()).slice(-2); // Last 2 digits
      const dateStr = `${day}${month}${year}`;

      // 4. First letter of last name (or 0 if no last name)
      const lastNameInitial = lastName ? lastName.charAt(0).toUpperCase() : '0';

      // 5. First digit of mobile number
      const firstPhone = phoneDigits.charAt(0) || '0';

      // Construct Bondhu ID: B-[NAME2][PHONE4][DDMMYY][INITIAL][PHONE1]
      return `B-${firstNamePart}${last4Phone}${dateStr}${lastNameInitial}${firstPhone}`;
    } catch (error) {
      console.error('Error generating Bondhu ID:', error);
      return 'B-ERROR';
    }
  }, [user?.name, user?.phone, user?.createdAt]);

  // Calculate member since date
  const memberSince = useMemo(() => {
    if (!user?.createdAt) return 'N/A';
    const date = user.createdAt;
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  }, [user?.createdAt]);

  // Get max value for chart scaling (both earnings and withdrawals)
  const maxValue = useMemo(() => {
    if (activityData.length === 0) return 1000;
    const allValues = activityData.flatMap(d => [d.earnings, d.withdrawals]);
    const max = Math.max(...allValues, 0);
    return max > 0 ? max : 1000; // Minimum 1000 for better visualization
  }, [activityData]);

  // External links data (same as main page)
  const externalLinks = useMemo(() => [
    {
      icon: DocumentTextIcon,
      label: t('dashboard.termsAndConditions'),
      color: '#ee4238',
      url: '/terms', // Navigate to full T&C page
      description: 'TO view T&C',
      isInternal: true // Flag to indicate this is an internal route
    },
    {
      icon: PaintBrushIcon,
      label: t('dashboard.products'),
      color: '#10b981',
      url: 'https://scipaints.in/products/',
      description: t('dashboard.exploreProducts')
    },
    {
      icon: SwatchIcon,
      label: t('dashboard.colourCombo'),
      color: '#3b82f6',
      url: 'https://scipaints.in/colour-combo/',
      description: t('dashboard.colorCombinations')
    },
    {
      icon: PhoneIcon,
      label: t('dashboard.contact'),
      color: '#f59e0b',
      url: 'https://scipaints.in/contact/',
      description: t('dashboard.getInTouch')
    },
  ], [t]);

  // Schemes state
  const [schemes, setSchemes] = useState<any[]>([]);
  const [schemesLoading, setSchemesLoading] = useState(true);

  // Load schemes from Firestore
  useEffect(() => {
    const loadSchemes = async () => {
      try {
        setSchemesLoading(true);
        const schemesQuery = query(
          collection(db, 'schemes'),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(schemesQuery);
        const schemesData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
          };
        }).filter((scheme: any) =>
          !scheme.targetApp || scheme.targetApp === 'painter' || scheme.targetApp === 'both'
        );
        setSchemes(schemesData);
      } catch (error) {
        console.error('Error loading schemes:', error);
      } finally {
        setSchemesLoading(false);
      }
    };

    loadSchemes();
  }, []);

  // Handle scheme download
  const handleDownloadScheme = async (scheme: any) => {
    try {
      // Check if fileUrl is valid
      if (!scheme.fileUrl || scheme.fileUrl === 'placeholder-url') {
        alert(t('dashboard.fileNotAvailable'));
        return;
      }

      // For Firebase Storage URLs, we need to handle them differently
      if (scheme.fileUrl.includes('firebasestorage.googleapis.com')) {
        // Firebase Storage URL - download directly
        const link = document.createElement('a');
        link.href = scheme.fileUrl;
        link.download = scheme.fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // External URL - open in new tab
        window.open(scheme.fileUrl, '_blank');
      }

      // Update download count in Firestore
      await updateDoc(doc(db, 'schemes', scheme.id), {
        downloadCount: (scheme.downloadCount || 0) + 1
      });
    } catch (error) {
      console.error('Error downloading scheme:', error);
      alert(t('dashboard.failedToDownload'));
    }
  };

  if (!user || loading.homeContent) {
    return <LoadingSpinner />;
  }

  // Available balance: use same formula as Payment / Withdrawal page
  const currentBalance = user.cashbackValue || 0;
  const pendingPaymentRequests = paymentRequests.filter(req => req.status === 'pending');
  const totalPendingAmount = pendingPaymentRequests.reduce(
    (sum, req) => sum + (req.amount || 0),
    0
  );
  const availableBalance = Math.max(0, currentBalance - totalPendingAmount);

  return (
    <div
      className={screenWidth >= 768 ? 'hide-scrollbar-desktop' : ''}
      style={{
        minHeight: '100vh',
        width: '100vw',
        background: '#eef3f9',
        paddingTop: '0px',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 80px)',
        margin: 0,
        overflowX: 'hidden',
        overflowY: 'auto'
      }}
    >
      <div
        className="space-y-6"
        style={{
          width: '100%',
          paddingTop: screenWidth < 768 ? '16px' : '24px',
          paddingBottom: screenWidth < 768 ? '16px' : '24px',
          paddingLeft: '0px',
          paddingRight: '0px',
          maxWidth: '100%'
        }}
      >

        {/* Profile Card */}
        <div className="bg-white p-4 shadow-card" style={{ borderRadius: '16px' }}>
          {/* Top Section: DP, Name, Status */}
          <div className="flex items-start gap-3 mb-4">
            {/* Profile Picture */}
            <div className="relative">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#ee4238] to-[#d63a32] p-1">
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserIcon className="w-7 h-7 text-gray-400" />
                  )}
                </div>
              </div>
              {/* Active Status Indicator */}
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
            </div>

            {/* Name and Status */}
            <div className="flex-1">
              <h2 className="text-lg font-black text-gray-900">{user.name || t('dashboard.user')}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="bg-gradient-to-r from-[#ee4238] to-[#d63a32] text-white text-xs font-bold px-2.5 py-0.5 rounded-full">
                  {user.accountType || t('dashboard.painter')}
                </div>
              </div>
            </div>
          </div>

          {/* Middle Section: Total Earnings */}
          <div
            className="relative bg-gradient-to-br from-[#ee4238] to-[#d63a32] rounded-2xl overflow-hidden mb-3"
            style={{ padding: screenWidth < 360 ? '12px' : screenWidth < 414 ? '14px' : '16px' }}
          >
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8"></div>

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <BanknotesIcon
                    className="text-white/80"
                    style={{ width: screenWidth < 360 ? '14px' : '16px', height: screenWidth < 360 ? '14px' : '16px' }}
                  />
                  <span
                    className="text-white/90 font-semibold uppercase tracking-wide"
                    style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
                  >
                    {t('dashboard.totalEarnings')}
                  </span>
                </div>
                <button
                  onClick={() => setShowBalance(!showBalance)}
                  className="bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-all backdrop-blur-sm"
                  style={{
                    width: screenWidth < 360 ? '28px' : '32px',
                    height: screenWidth < 360 ? '28px' : '32px'
                  }}
                >
                  {showBalance ? (
                    <EyeIcon
                      className="text-white"
                      style={{ width: screenWidth < 360 ? '14px' : '16px', height: screenWidth < 360 ? '14px' : '16px' }}
                    />
                  ) : (
                    <EyeSlashIcon
                      className="text-white"
                      style={{ width: screenWidth < 360 ? '14px' : '16px', height: screenWidth < 360 ? '14px' : '16px' }}
                    />
                  )}
                </button>
              </div>

              {loadingActivity ? (
                <span
                  className="font-black text-white"
                  style={{ fontSize: screenWidth < 360 ? '18px' : screenWidth < 414 ? '20px' : '24px' }}
                >
                  {t('dashboard.loading')}
                </span>
              ) : (
                <div
                  className="font-black text-white mb-2"
                  style={{ fontSize: screenWidth < 360 ? '24px' : screenWidth < 414 ? '28px' : '32px' }}
                >
                  ₹{showBalance ? totalEarnings.toLocaleString('en-IN') : '••••••'}
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-white/20">
                <span
                  className="text-white/70 font-medium"
                  style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
                >
                  {t('dashboard.availableBalance')}
                </span>
                <span
                  className="text-white font-bold"
                  style={{ fontSize: screenWidth < 360 ? '11px' : '14px' }}
                >
                  ₹{showBalance ? availableBalance.toLocaleString('en-IN') : '••••••'}
                </span>
              </div>
            </div>
          </div>

          {/* Bottom Section: Member Since & Bondhu ID */}
          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns: screenWidth < 360 ? '1fr' : 'repeat(2, 1fr)',
              gap: screenWidth < 360 ? '8px' : '8px'
            }}
          >
            <div
              className="bg-gray-50 rounded-xl"
              style={{ padding: screenWidth < 360 ? '10px' : '12px' }}
            >
              <div
                className="flex items-center gap-1.5 text-gray-500 mb-0.5"
                style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
              >
                <CalendarIcon
                  style={{ width: screenWidth < 360 ? '12px' : '14px', height: screenWidth < 360 ? '12px' : '14px' }}
                />
                <span>{t('dashboard.memberSince')}</span>
              </div>
              <div
                className="text-gray-900 font-bold"
                style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
              >
                {memberSince}
              </div>
            </div>
            <div
              className="bg-gray-50 rounded-xl"
              style={{ padding: screenWidth < 360 ? '10px' : '12px' }}
            >
              <div
                className="flex items-center gap-1.5 text-gray-500 mb-0.5"
                style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
              >
                <IdentificationIcon
                  style={{ width: screenWidth < 360 ? '12px' : '14px', height: screenWidth < 360 ? '12px' : '14px' }}
                />
                <span>{t('dashboard.bondhuId')}</span>
              </div>
              <div
                className="text-gray-900 font-bold"
                style={{
                  fontSize: screenWidth < 360 ? '9px' : '11px',
                  wordBreak: 'break-all'
                }}
              >
                {bondhuId}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Graph */}
        <div
          className="bg-white shadow-card"
          style={{
            borderRadius: '16px',
            padding: screenWidth < 360 ? '12px' : '16px'
          }}
        >
          <div
            className="flex items-center justify-between mb-4"
            style={{
              flexDirection: screenWidth < 360 ? 'column' : 'row',
              gap: screenWidth < 360 ? '12px' : '0',
              alignItems: screenWidth < 360 ? 'stretch' : 'center'
            }}
          >
            <h3
              className="font-black text-gray-900 flex items-center gap-1.5"
              style={{ fontSize: screenWidth < 360 ? '14px' : '16px' }}
            >
              <ChartBarIcon
                className="text-[#ee4238]"
                style={{ width: screenWidth < 360 ? '16px' : '18px', height: screenWidth < 360 ? '16px' : '18px' }}
              />
              {t('dashboard.activityOverview')}
            </h3>
            <select
              value={activityPeriod}
              onChange={(e) => setActivityPeriod(e.target.value as '3months' | '6months' | 'year')}
              className="font-semibold text-gray-600 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-[#ee4238] focus:border-transparent"
              style={{
                fontSize: screenWidth < 360 ? '10px' : '12px',
                padding: screenWidth < 360 ? '6px 8px' : '8px 12px',
                width: screenWidth < 360 ? '100%' : 'auto'
              }}
            >
              <option value="3months">{t('dashboard.last3Months')}</option>
              <option value="6months">{t('dashboard.last6Months')}</option>
              <option value="year">{t('dashboard.thisYear')}</option>
            </select>
          </div>

          {/* Chart */}
          <div style={{
            maxHeight: screenWidth < 360 ? '180px' : '200px',
            minHeight: screenWidth < 360 ? '140px' : '160px',
            position: 'relative',
            width: '100%',
            overflowX: activityData.length > 6 ? 'auto' : 'visible',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch'
          }}>
            {loadingActivity ? (
              <div className="flex items-center justify-center" style={{ height: screenWidth < 360 ? '140px' : '160px' }}>
                <div
                  className="animate-spin border-2 border-gray-300 border-t-[#ee4238] rounded-full"
                  style={{
                    width: screenWidth < 360 ? '24px' : '28px',
                    height: screenWidth < 360 ? '24px' : '28px'
                  }}
                ></div>
              </div>
            ) : activityData.length === 0 ? (
              <div
                className="flex items-center justify-center text-gray-400 text-center"
                style={{
                  height: screenWidth < 360 ? '140px' : '160px',
                  fontSize: screenWidth < 360 ? '10px' : '12px'
                }}
              >
                {t('dashboard.noActivityData')}
              </div>
            ) : (
              <div
                className="flex items-end"
                style={{
                  height: screenWidth < 360 ? '100px' : '120px',
                  maxHeight: screenWidth < 360 ? '100px' : '120px',
                  gap: activityData.length > 6 ? (screenWidth < 360 ? '4px' : '6px') : (screenWidth < 360 ? '6px' : '8px'),
                  minWidth: activityData.length > 6 ? 'max-content' : '100%',
                  paddingRight: activityData.length > 6 ? (screenWidth < 360 ? '12px' : '16px') : '0',
                  paddingBottom: '20px',
                  justifyContent: activityData.length > 6 ? 'flex-start' : 'space-between'
                }}
              >
                {activityData.map((data, index) => {
                  const earningsHeight = Math.max((data.earnings / maxValue) * 100, 2);
                  const withdrawalsHeight = Math.max((data.withdrawals / maxValue) * 100, 2);
                  const barWidth = screenWidth < 360
                    ? (activityData.length > 6 ? '10px' : '14px')
                    : (activityData.length > 6 ? '12px' : '16px');
                  const barGap = screenWidth < 360 ? '2px' : '3px';

                  return (
                    <div
                      key={index}
                      className="flex flex-col items-center"
                      style={{
                        minWidth: activityData.length > 6
                          ? (screenWidth < 360 ? '32px' : '36px')
                          : 'auto',
                        flex: activityData.length > 6 ? '0 0 auto' : '1 1 0',
                        gap: screenWidth < 360 ? '6px' : '8px'
                      }}
                    >
                      {/* Bars Container */}
                      <div
                        className="flex items-end"
                        style={{
                          width: '100%',
                          height: screenWidth < 360 ? '100px' : '120px',
                          maxHeight: screenWidth < 360 ? '100px' : '120px',
                          gap: barGap,
                          justifyContent: 'center',
                          alignItems: 'flex-end'
                        }}
                      >
                        {/* Earnings Bar (Green) */}
                        <div
                          className="bg-gradient-to-t from-green-500 to-green-400 rounded-t transition-all duration-500 ease-out hover:opacity-80 cursor-pointer relative"
                          style={{
                            height: `${earningsHeight}%`,
                            width: barWidth,
                            minHeight: '4px',
                            maxHeight: '100%'
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                            setActiveTooltipIndex(index);
                          }}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            setTimeout(() => setActiveTooltipIndex(null), 2000);
                          }}
                          onMouseEnter={() => setActiveTooltipIndex(index)}
                          onMouseLeave={() => setActiveTooltipIndex(null)}
                        >
                          {/* Tooltip */}
                          {activeTooltipIndex === index && (
                            <div
                              className="absolute left-1/2 -translate-x-1/2 transition-opacity whitespace-nowrap z-50 pointer-events-none"
                              style={{
                                top: '100%',
                                marginTop: '4px',
                                padding: screenWidth < 360 ? '3px 6px' : '4px 8px',
                                fontSize: screenWidth < 360 ? '9px' : '10px',
                                backgroundColor: '#16a34a',
                                color: 'white',
                                fontWeight: '700',
                                borderRadius: '6px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
                                transform: 'translateX(-50%)',
                                opacity: 1
                              }}
                            >
                              ₹{data.earnings.toLocaleString('en-IN')}
                            </div>
                          )}
                        </div>

                        {/* Withdrawals Bar (Red) */}
                        <div
                          className="bg-gradient-to-t from-red-500 to-red-400 rounded-t transition-all duration-500 ease-out hover:opacity-80 cursor-pointer relative"
                          style={{
                            height: `${withdrawalsHeight}%`,
                            width: barWidth,
                            minHeight: '4px',
                            maxHeight: '100%'
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                            setActiveTooltipIndex(index + 1000); // Use offset to differentiate from earnings
                          }}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            setTimeout(() => setActiveTooltipIndex(null), 2000);
                          }}
                          onMouseEnter={() => setActiveTooltipIndex(index + 1000)}
                          onMouseLeave={() => setActiveTooltipIndex(null)}
                        >
                          {/* Tooltip */}
                          {activeTooltipIndex === index + 1000 && (
                            <div
                              className="absolute left-1/2 -translate-x-1/2 transition-opacity whitespace-nowrap z-50 pointer-events-none"
                              style={{
                                top: '100%',
                                marginTop: '4px',
                                padding: screenWidth < 360 ? '3px 6px' : '4px 8px',
                                fontSize: screenWidth < 360 ? '9px' : '10px',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                fontWeight: '700',
                                borderRadius: '6px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
                                transform: 'translateX(-50%)',
                                opacity: 1
                              }}
                            >
                              ₹{data.withdrawals.toLocaleString('en-IN')}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Month Label */}
                      <span
                        className="font-semibold text-gray-500 text-center whitespace-nowrap"
                        style={{
                          fontSize: screenWidth < 360 ? '9px' : activityData.length > 6 ? '10px' : '11px',
                          lineHeight: '1.2',
                          marginTop: 'auto'
                        }}
                      >
                        {data.month}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Legend - Always visible */}
          <div
            className="flex items-center justify-center gap-3 mt-3 pt-3 border-t border-gray-100"
            style={{ gap: screenWidth < 360 ? '12px' : '16px' }}
          >
            <div className="flex items-center gap-1.5">
              <div
                className="bg-gradient-to-t from-green-500 to-green-400 rounded"
                style={{
                  width: screenWidth < 360 ? '10px' : '12px',
                  height: screenWidth < 360 ? '10px' : '12px'
                }}
              ></div>
              <span
                className="font-semibold text-gray-600"
                style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
              >
                {t('dashboard.earnings')}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <div
                className="bg-gradient-to-t from-red-500 to-red-400 rounded"
                style={{
                  width: screenWidth < 360 ? '10px' : '12px',
                  height: screenWidth < 360 ? '10px' : '12px'
                }}
              ></div>
              <span
                className="font-semibold text-gray-600"
                style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
              >
                {t('dashboard.withdrawals')}
              </span>
            </div>
          </div>
        </div>

        {/* What's New Section - Image Slider */}
        {slidesWithImages.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                <SparklesIcon className="w-5 h-5 text-[#ee4238]" />
                {t('dashboard.whatsNew')}
              </h3>
              <div className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                {t('dashboard.live')}
              </div>
            </div>

            {/* Image Carousel */}
            <div
              className="relative bg-white overflow-hidden shadow-card"
              style={{ borderRadius: '16px' }}
              onMouseEnter={() => setIsAutoPlaying(false)}
              onMouseLeave={() => setIsAutoPlaying(true)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Slides Container - 4:3 Aspect Ratio */}
              <div className="relative w-full" style={{ paddingBottom: '75%' }}>
                {slidesWithImages.map((ad, index) => (
                  <div
                    key={ad.id}
                    className={`absolute top-0 left-0 w-full h-full transition-all duration-500 ease-in-out ${index === currentSlide
                        ? 'opacity-100 translate-x-0'
                        : index < currentSlide
                          ? 'opacity-0 -translate-x-full'
                          : 'opacity-0 translate-x-full'
                      }`}
                    onClick={() => incrementAdView(ad.id)}
                  >
                    <img
                      src={ad.imageUrl}
                      alt={ad.title || `Offer ${index + 1}`}
                      className="absolute top-0 left-0 w-full h-full object-cover cursor-pointer"
                      draggable={false}
                    />
                  </div>
                ))}
              </div>

              {/* Navigation Dots */}
              <div
                className="absolute left-0 right-0 flex items-center justify-center gap-2 z-10"
                style={{
                  bottom: screenWidth < 360 ? '12px' : '16px',
                  gap: screenWidth < 360 ? '6px' : '8px'
                }}
              >
                {slidesWithImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`transition-all duration-300 rounded-full ${index === currentSlide
                        ? 'bg-white'
                        : 'bg-white/50 hover:bg-white/75'
                      }`}
                    style={{
                      width: index === currentSlide ? (screenWidth < 360 ? '24px' : '32px') : (screenWidth < 360 ? '6px' : '8px'),
                      height: screenWidth < 360 ? '6px' : '8px'
                    }}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>

              {/* Slide Counter */}
              <div
                className="absolute bg-black/50 text-white font-bold rounded-full backdrop-blur-sm"
                style={{
                  top: screenWidth < 360 ? '12px' : '16px',
                  right: screenWidth < 360 ? '12px' : '16px',
                  fontSize: screenWidth < 360 ? '10px' : '12px',
                  padding: screenWidth < 360 ? '4px 8px' : '6px 12px'
                }}
              >
                {currentSlide + 1} / {slidesWithImages.length}
              </div>
            </div>
          </div>
        )}

        {/* Schemes Section */}
        <div className="space-y-3">
          <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
            <GiftIcon className="w-5 h-5 text-[#ee4238]" />
            {t('dashboard.schemes')}
          </h3>

          <div className="space-y-3">
            {schemesLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ee4238] mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">{t('dashboard.loadingSchemes')}</p>
              </div>
            ) : schemes.length === 0 ? (
              <div className="text-center py-8">
                <div style={{ fontSize: screenWidth < 360 ? '32px' : '48px' }} className="mb-2">📋</div>
                <p
                  className="text-gray-500"
                  style={{ fontSize: screenWidth < 360 ? '12px' : '14px' }}
                >
                  {t('dashboard.noSchemesAvailable')}
                </p>
              </div>
            ) : (
              schemes.map((scheme, index) => (
                <div
                  key={scheme.id}
                  className="bg-white rounded-2xl shadow-card hover:shadow-lg transition-all duration-300 cursor-pointer"
                  style={{ padding: screenWidth < 360 ? '12px' : '16px' }}
                  onClick={() => handleDownloadScheme(scheme)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="bg-gradient-to-br from-[#ee4238] to-[#d63a32] rounded-xl flex items-center justify-center"
                      style={{
                        width: screenWidth < 360 ? '40px' : '48px',
                        height: screenWidth < 360 ? '40px' : '48px'
                      }}
                    >
                      {scheme.fileType === 'pdf' ? (
                        <DocumentTextIcon
                          className="text-white"
                          style={{ width: screenWidth < 360 ? '20px' : '24px', height: screenWidth < 360 ? '20px' : '24px' }}
                        />
                      ) : (
                        <PaintBrushIcon
                          className="text-white"
                          style={{ width: screenWidth < 360 ? '20px' : '24px', height: screenWidth < 360 ? '20px' : '24px' }}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4
                        className="font-bold text-gray-900"
                        style={{ fontSize: screenWidth < 360 ? '12px' : '14px' }}
                      >
                        {scheme.title}
                      </h4>
                      <p
                        className="text-gray-600"
                        style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
                      >
                        {scheme.description}
                      </p>
                      <div
                        className="flex items-center gap-2 mt-1"
                        style={{
                          flexDirection: screenWidth < 360 ? 'column' : 'row',
                          alignItems: screenWidth < 360 ? 'flex-start' : 'center',
                          gap: screenWidth < 360 ? '4px' : '8px'
                        }}
                      >
                        <span
                          className="text-gray-500"
                          style={{ fontSize: screenWidth < 360 ? '9px' : '11px' }}
                        >
                          📁 {scheme.fileName}
                        </span>
                        <span
                          className="text-gray-500"
                          style={{ fontSize: screenWidth < 360 ? '9px' : '11px' }}
                        >
                          📥 {scheme.downloadCount || 0} {t('dashboard.downloads')}
                        </span>
                      </div>
                    </div>
                    <div className="text-[#ee4238] flex-shrink-0">
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        style={{ width: screenWidth < 360 ? '18px' : '20px', height: screenWidth < 360 ? '18px' : '20px' }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* External Links Section */}
        <div className="space-y-3">
          <h3
            className="font-black text-gray-900 flex items-center gap-2"
            style={{
              fontSize: screenWidth < 360 ? '16px' : '18px',
              gap: screenWidth < 360 ? '8px' : '12px'
            }}
          >
            <BanknotesIcon
              className="text-[#ee4238]"
              style={{ width: screenWidth < 360 ? '18px' : '20px', height: screenWidth < 360 ? '18px' : '20px' }}
            />
            {t('dashboard.quickLinks')}
          </h3>

          <div
            className="grid gap-3"
            style={{
              gridTemplateColumns: screenWidth < 360 ? '1fr' : 'repeat(2, 1fr)',
              gap: screenWidth < 360 ? '12px' : '16px'
            }}
          >
            {externalLinks.map((link, index) => {
              const Icon = link.icon;

              // Handle internal links (navigate to route)
              if (link.isInternal) {
                return (
                  <button
                    key={index}
                    onClick={() => navigate(link.url)}
                    className="bg-white rounded-2xl shadow-soft hover:shadow-card transition-all flex flex-col items-center touch-manipulation group"
                    style={{
                      padding: screenWidth < 360 ? '12px' : '16px',
                      gap: screenWidth < 360 ? '8px' : '12px',
                      minHeight: screenWidth < 360 ? '120px' : '140px',
                      height: '100%'
                    }}
                  >
                    <div
                      className="rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: `${link.color}15`,
                        width: screenWidth < 360 ? '40px' : '48px',
                        height: screenWidth < 360 ? '40px' : '48px'
                      }}
                    >
                      <Icon
                        style={{
                          color: link.color,
                          width: screenWidth < 360 ? '20px' : '24px',
                          height: screenWidth < 360 ? '20px' : '24px'
                        }}
                      />
                    </div>
                    <span
                      className="font-semibold text-gray-700 text-center flex-shrink-0"
                      style={{ fontSize: screenWidth < 360 ? '11px' : '12px' }}
                    >
                      {link.label}
                    </span>
                    <span
                      className="text-gray-500 text-center flex-shrink-0"
                      style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
                    >
                      {link.description}
                    </span>
                  </button>
                );
              }

              // Handle external links
              return (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white rounded-2xl shadow-soft hover:shadow-card transition-all flex flex-col items-center touch-manipulation group"
                  style={{
                    padding: screenWidth < 360 ? '12px' : '16px',
                    gap: screenWidth < 360 ? '8px' : '12px',
                    minHeight: screenWidth < 360 ? '120px' : '140px',
                    height: '100%'
                  }}
                >
                  <div
                    className="rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: `${link.color}15`,
                      width: screenWidth < 360 ? '40px' : '48px',
                      height: screenWidth < 360 ? '40px' : '48px'
                    }}
                  >
                    <Icon
                      style={{
                        color: link.color,
                        width: screenWidth < 360 ? '20px' : '24px',
                        height: screenWidth < 360 ? '20px' : '24px'
                      }}
                    />
                  </div>
                  <span
                    className="font-semibold text-gray-700 text-center flex-shrink-0"
                    style={{ fontSize: screenWidth < 360 ? '11px' : '12px' }}
                  >
                    {link.label}
                  </span>
                  <span
                    className="text-gray-500 text-center flex-shrink-0"
                    style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
                  >
                    {link.description}
                  </span>
                </a>
              );
            })}
          </div>
        </div>


        {/* Support Section */}
        <div className="bg-white p-5 shadow-card" style={{ borderRadius: '16px' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="font-bold text-gray-900 text-sm">Need Help?</div>
                <div className="text-xs text-gray-500">Contact Support</div>
              </div>
            </div>
            <button
              onClick={() => setShowChatPopup(true)}
              className="bg-[#ee4238] text-white text-sm font-bold px-4 py-2 rounded-xl hover:bg-[#d63a32] transition-colors"
            >
              Chat Now
            </button>
          </div>
        </div>

      </div>

      {/* Chat Popup Window */}
      {showChatPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" style={{ padding: screenWidth < 360 ? '8px' : '16px' }}>
          <div
            className="bg-white rounded-3xl shadow-2xl w-full flex flex-col animate-in fade-in zoom-in duration-200"
            style={{
              maxWidth: 'min(448px, calc(100vw - 16px))',
              height: screenWidth < 360 ? '85vh' : '80vh'
            }}
          >
            {/* Chat Header */}
            <div
              className="bg-gradient-to-r from-[#ee4238] to-[#d63a32] text-white rounded-t-3xl flex items-center justify-between"
              style={{ padding: screenWidth < 360 ? '12px' : '16px' }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="bg-white/20 rounded-full flex items-center justify-center"
                  style={{
                    width: screenWidth < 360 ? '36px' : '40px',
                    height: screenWidth < 360 ? '36px' : '40px'
                  }}
                >
                  <ChatBubbleLeftRightIcon
                    style={{ width: screenWidth < 360 ? '18px' : '20px', height: screenWidth < 360 ? '18px' : '20px' }}
                  />
                </div>
                <div>
                  <h3
                    className="font-bold"
                    style={{ fontSize: screenWidth < 360 ? '14px' : '16px' }}
                  >
                    {t('dashboard.customerSupport')}
                  </h3>
                  <p
                    className="text-white/80"
                    style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
                  >
                    {t('dashboard.weAreHereToHelp')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowChatPopup(false)}
                className="bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                style={{
                  width: screenWidth < 360 ? '32px' : '36px',
                  height: screenWidth < 360 ? '32px' : '36px'
                }}
              >
                <XMarkIcon style={{ width: screenWidth < 360 ? '18px' : '20px', height: screenWidth < 360 ? '18px' : '20px' }} />
              </button>
            </div>

            {/* Chat Messages Area */}
            <div
              className="flex-1 overflow-y-auto bg-gray-50"
              style={{ padding: screenWidth < 360 ? '12px' : '16px' }}
            >
              <div className="space-y-3">
                {/* Welcome Message */}
                <div className="flex gap-2">
                  <div
                    className="bg-[#ee4238] rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      width: screenWidth < 360 ? '32px' : '36px',
                      height: screenWidth < 360 ? '32px' : '36px'
                    }}
                  >
                    <span
                      className="text-white font-bold"
                      style={{ fontSize: screenWidth < 360 ? '10px' : '12px' }}
                    >
                      SP
                    </span>
                  </div>
                  <div
                    className="bg-white rounded-2xl rounded-tl-none shadow-sm"
                    style={{
                      padding: screenWidth < 360 ? '10px' : '12px',
                      maxWidth: '80%'
                    }}
                  >
                    <p
                      className="text-gray-800"
                      style={{ fontSize: screenWidth < 360 ? '12px' : '14px' }}
                    >
                      {t('dashboard.hello')}
                    </p>
                    <p
                      className="text-gray-800 mt-1"
                      style={{ fontSize: screenWidth < 360 ? '12px' : '14px' }}
                    >
                      {t('dashboard.howCanWeHelp')}
                    </p>
                    <p
                      className="font-semibold text-[#ee4238] mt-2"
                      style={{ fontSize: screenWidth < 360 ? '9px' : '11px' }}
                    >
                      {t('dashboard.featuresComingSoon')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Input Area */}
            <div
              className="bg-white border-t border-gray-200 rounded-b-3xl"
              style={{ padding: screenWidth < 360 ? '12px' : '16px' }}
            >
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (chatMessage.trim()) {
                    alert(t('dashboard.demoModeMessage') + chatMessage);
                    setChatMessage('');
                  }
                }}
                className="flex gap-2"
                style={{ gap: screenWidth < 360 ? '8px' : '12px' }}
              >
                <input
                  type="text"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  placeholder={t('dashboard.typeYourMessage')}
                  className="flex-1 bg-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#ee4238]"
                  style={{
                    padding: screenWidth < 360 ? '8px 12px' : '12px 16px',
                    fontSize: screenWidth < 360 ? '12px' : '14px'
                  }}
                />
                <button
                  type="submit"
                  className="bg-[#ee4238] text-white rounded-2xl flex items-center justify-center hover:bg-[#d63a32] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    width: screenWidth < 360 ? '40px' : '48px',
                    height: screenWidth < 360 ? '40px' : '48px'
                  }}
                  disabled={true}
                >
                  <PaperAirplaneIcon style={{ width: screenWidth < 360 ? '18px' : '20px', height: screenWidth < 360 ? '18px' : '20px' }} />
                </button>
              </form>
              <p
                className="text-gray-500 text-center mt-2"
                style={{ fontSize: screenWidth < 360 ? '9px' : '11px' }}
              >
                {t('dashboard.poweredBy')}
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard; 
